import React, { useContext } from "react";
import {
    ConfirmDialog,
    createFailure,
    createListTableColumns,
    createValidationResult,
    ensure,
    FailureSeverity,
    FormValidationSummary,
    ShimmeredDetailsList,
    useNotifications,
    usePerformMultipleActions,
    ValidationFailure,
    Text,
    renderTextWithTooltip
} from "sprint-common-web";

import { useCloudFileApi } from "../../../../../common/api/master-api/cloud-files/useCloudFileApi";
import { renderActionStatus } from "../../../../../common/utils/renderActionStatus";
import { CloudFileListingContext } from "../../CloudFileListingContext";
import { ApproveNotificationsItem, ProcessApproveNotificationsItem } from "./ApproveNotificationsItem";

interface ApproveNotificationsDialogProps {
    isOpen: boolean;
    onClose(): void;
    selectedItems: ApproveNotificationsItem[];
}

export const ApproveCloudFileNotificationsDialog: React.VFC<ApproveNotificationsDialogProps> = ({
    isOpen,
    onClose,
    selectedItems
}) => {
    const { approveCloudFileNotifications } = useCloudFileApi();

    const { approveNotifications: approveNotificationsInContext } = useContext(CloudFileListingContext);

    const { success, error } = useNotifications();

    const { performAllItems, isActionInProgress, items } = usePerformMultipleActions<ApproveNotificationsItem>({
        isAvailable: (item) => {
            return !item.isNotificationApproved;
        },
        selectedItems
    });

    const failure = validateDiscardedItems(items, selectedItems.length);

    const validationResult = createValidationResult([failure]);

    const handleApprove = async () => {
        const results = await approveAllItems();

        if (results.every(({ status }) => status === "fulfilled")) {
            handleClose();

            success("Buzz notifications approved");
        } else error("Buzz notifications approve failed");
    };

    const approveAllItems = async () => {
        return performAllItems(async (item) => {
            await approveCloudFileNotifications({ cloudFileId: item.id });

            approveNotificationsInContext(item.id);
        });
    };

    const handleClose = () => {
        onClose();
    };

    return (
        <ConfirmDialog
            title="Approve buzz notifications"
            text="Are you sure you want to approve buzz notifications for given cloud files? Once enabled, they cannot be turned off."
            hidden={!isOpen}
            isBlocking={isActionInProgress}
            width={"65%"}
            contentHeight={500}
            confirm={{ onClick: handleApprove, disabled: !items.length }}
            cancel={{ onClick: handleClose }}
        >
            <FormValidationSummary validationResult={validationResult} />
            {!!items.length && <ApproveNotificationsSummary items={items} />}
        </ConfirmDialog>
    );
};

const discardedCloudFilesInfoMessage = `Cloud files with already approved buzz notifications have been discarded`;

interface ApproveNotificationsSummaryProps {
    items: ApproveNotificationsItem[];
}

const ApproveNotificationsSummary: React.VFC<ApproveNotificationsSummaryProps> = ({
    items
}: ApproveNotificationsSummaryProps) => {
    const columns = createListTableColumns<ProcessApproveNotificationsItem>([
        {
            type: "basic",
            key: "fileName",
            name: "File name",
            minWidth: 200,
            maxWidth: 350,
            onRender: renderTextWithTooltip
        },
        {
            type: "basic",
            key: "filePath",
            name: "File path",
            minWidth: 200,
            maxWidth: 450,
            onRender: renderFilePath
        },
        {
            type: "basic",
            key: "status",
            name: "Status",
            minWidth: 200,
            maxWidth: 200
        },
        {
            type: "basic",
            key: "processStatus",
            name: "Process status",
            minWidth: 80,
            maxWidth: 90,
            onRender: renderActionStatus
        }
    ]);

    return <ShimmeredDetailsList columns={columns} items={items} layoutMode="justified" />;
};

const validateDiscardedItems = (
    items: ApproveNotificationsItem[],
    selectedItemsCount: number
): ValidationFailure | void => {
    if (!items.length) {
        return createFailure(
            `${discardedCloudFilesInfoMessage}. Nothing to approve.`,
            "no-cloud-files-to-approve-notifications",
            FailureSeverity.Error
        );
    } else if (items.length < selectedItemsCount) {
        return createFailure(
            discardedCloudFilesInfoMessage,
            "cloud-files-discarded-from-notifications-approval",
            FailureSeverity.Warning
        );
    }
};

const renderFilePath = (item?: ApproveNotificationsItem) => {
    ensure(item);

    return <Text variant="small" tooltip={item.filePath} value={item.filePath ?? "-"} />;
};
